import type { Dependency, DependencyType, Task, TaskId } from '@bigint-project/core';
import { formatTaskRef, getTaskPredecessors } from './dependency-column.js';
import { formatLag, parsePredecessorInput } from './predecessor.js';

/**
 * 前置任務下拉勾選面板的純邏輯層(不含任何 React / DOM)。
 *
 * 設計原則:面板**不自己組 Dependency**,而是把勾選結果組回與手動輸入完全相同的
 * 文字語法(例如 `3FS+2d,5SS`),再交給既有的 buildDependenciesFromInput 落地。
 * 這樣「勾選」與「打字」是同一條寫入路徑,不可能出現兩種結果不一致的情形,也自動
 * 沿用既有的 ref 解析、自我參照與循環檢查。
 */

/** 面板內單一筆已勾選的前置任務(尚未落地)。 */
export interface PredecessorDraft {
  readonly taskId: TaskId;
  readonly type: DependencyType;
  /** 與文字語法相同的 lag 片段,例如 `+2d`、`-1d`、`+3ed`;無 lag 時為空字串。 */
  readonly lagText: string;
}

/** 下拉清單中的一列候選任務。 */
export interface PredecessorCandidate {
  readonly taskId: TaskId;
  /** 完整任務清單中的列號(1-based);僅供搜尋比對與穩定的 DOM key 使用。 */
  readonly rowNumber: number;
  readonly wbs: string;
  /** 寫進文字語法的識別碼(WBS 碼;WBS 為空時退回列號),見 formatTaskRef。 */
  readonly ref: string;
  readonly name: string;
  readonly selected: boolean;
}

export const DEPENDENCY_TYPES: readonly DependencyType[] = ['FS', 'SS', 'FF', 'SF'];

/**
 * 取得「不可被選為前置任務」的 id 集合:自己 + 所有(遞移)後續任務。
 *
 * 文字輸入路徑只擋單層的雙節點循環(A→B 且 B→A),更深的循環留給排程引擎報錯;
 * 下拉清單是**用選的**,把明顯會爆的選項留在清單裡只會讓人踩坑,因此這裡做完整的
 * 後繼閉包,選不到就不會選錯。
 */
export function collectForbiddenPredecessorIds(
  taskId: TaskId,
  dependencies: readonly Dependency[],
): ReadonlySet<TaskId> {
  const forbidden = new Set<TaskId>([taskId]);
  const queue: TaskId[] = [taskId];

  while (queue.length > 0) {
    const current = queue.shift();
    if (current === undefined) break;
    for (const dependency of dependencies) {
      if (dependency.predecessorId !== current) continue;
      if (forbidden.has(dependency.successorId)) continue;
      forbidden.add(dependency.successorId);
      queue.push(dependency.successorId);
    }
  }

  return forbidden;
}

function matchesFilter(task: Task, rowNumber: number, filter: string): boolean {
  const needle = filter.trim().toLowerCase();
  if (needle === '') return true;
  return (
    String(rowNumber).includes(needle) ||
    task.name.toLowerCase().includes(needle) ||
    (task.wbs ?? '').toLowerCase().includes(needle)
  );
}

/**
 * 組出下拉清單內容:排除自己與會造成循環者,再套用搜尋字串過濾。
 *
 * 清單顯示的 `ref` 就是打字時要輸入的識別碼(WBS 碼),兩條路徑完全一致。
 * `rowNumber` 只是搜尋用的輔助欄位,一律以**完整任務清單**(未過濾收合)的序號為準。
 */
export function buildPredecessorCandidates(
  taskId: TaskId,
  tasks: readonly Task[],
  dependencies: readonly Dependency[],
  drafts: readonly PredecessorDraft[],
  filter = '',
): readonly PredecessorCandidate[] {
  const forbidden = collectForbiddenPredecessorIds(taskId, dependencies);
  const selectedIds = new Set(drafts.map((draft) => draft.taskId));

  const candidates: PredecessorCandidate[] = [];
  tasks.forEach((task, index) => {
    if (forbidden.has(task.id)) return;
    const rowNumber = index + 1;
    if (!matchesFilter(task, rowNumber, filter)) return;
    candidates.push({
      taskId: task.id,
      rowNumber,
      wbs: task.wbs ?? '',
      ref: formatTaskRef(task.id, tasks),
      name: task.name,
      selected: selectedIds.has(task.id),
    });
  });
  return candidates;
}

/** 取出某筆依賴的 lag 片段文字(與 formatPredecessorList 完全同一套換算規則)。 */
export function formatLagText(dependency: Dependency): string {
  return formatLag(dependency.lagMinutes, dependency.lagIsElapsed ?? false);
}

/** 由目前的依賴狀態產生面板初始草稿。 */
export function draftsFromDependencies(
  taskId: TaskId,
  dependencies: readonly Dependency[],
  tasks: readonly Task[],
): readonly PredecessorDraft[] {
  return getTaskPredecessors(taskId, dependencies)
    .filter((dependency) => tasks.some((task) => task.id === dependency.predecessorId))
    .map((dependency) => ({
      taskId: dependency.predecessorId,
      type: dependency.type,
      lagText: formatLagText(dependency),
    }));
}

/** 勾選 / 取消勾選單一任務(勾選時預設 FS、無 lag)。 */
export function toggleDraft(
  drafts: readonly PredecessorDraft[],
  taskId: TaskId,
): readonly PredecessorDraft[] {
  if (drafts.some((draft) => draft.taskId === taskId)) {
    return drafts.filter((draft) => draft.taskId !== taskId);
  }
  return [...drafts, { taskId, type: 'FS', lagText: '' }];
}

/** 更新單一筆草稿的依賴類型或 lag。 */
export function updateDraft(
  drafts: readonly PredecessorDraft[],
  taskId: TaskId,
  patch: Partial<Omit<PredecessorDraft, 'taskId'>>,
): readonly PredecessorDraft[] {
  return drafts.map((draft) => (draft.taskId === taskId ? { ...draft, ...patch } : draft));
}

/**
 * 把草稿組回文字語法(與手動輸入完全相同的字串),交由 buildDependenciesFromInput 落地。
 * 找不到對應任務的草稿會被略過(formatTaskRef 會回傳 '?',那不是合法 ref)。
 */
export function draftsToInput(
  drafts: readonly PredecessorDraft[],
  tasks: readonly Task[],
): string {
  return drafts
    .map((draft) => {
      const ref = formatTaskRef(draft.taskId, tasks);
      if (ref === '?') return null;
      return `${ref}${draft.type}${draft.lagText.trim()}`;
    })
    .filter((part): part is string => part !== null)
    .join(',');
}

/** lag 欄位的即時檢查:空字串合法,其餘必須是合法的 `+2d` / `-1w` / `+3ed` 語法。 */
export function isValidLagText(lagText: string): boolean {
  const trimmed = lagText.trim();
  if (trimmed === '') return true;
  return parsePredecessorInput(`1FS${trimmed}`) !== null;
}
